import { signUpSourceLabel } from 'helpers/investor/getSignUpSourceLabel';
import { useInvestor } from 'hooks/data/investor/useInvestor';
import {
  AppropriatenessStatusLabel,
  getCurrentAppropriatenessStatusLabel,
} from 'further-ui/utils';
import RecordRow from 'components/RecordRow';
import Button from 'components/Button';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Investor = ApiResponses['investor']['getInvestor'];

type Props = {
  id: string;
  investorDetail: Pick<
    Investor,
    'signUpSource' | 'appropriatenessResults' | 'appropriatenessTestAttempts'
  >;
  downloadQuestion: () => void;
};

const InvestorAppropriateness: React.FC<Props> = ({
  downloadQuestion,
  id,
  investorDetail,
}) => {
  const resetAppropriatenessDisclosure = useDisclosure();
  const [canEdit] = useHasPermissions([RolePermission.EditInvestor]);

  const resetInvestorAppropriatenessMutation =
    api.investor.resetInvestorAppropriateness.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Appropriateness test results successfully reset.',
      }),
    );

  const { allowAppropriateness, manuallyPassAppropriateness } = useInvestor(id);

  const { appropriatenessResults, appropriatenessTestAttempts } =
    investorDetail;

  const appropriateness = appropriatenessResults?.length
    ? appropriatenessResults[0]
    : undefined;
  const hasAnswerList = appropriatenessTestAttempts?.length > 0;

  const appropriatenessPassCount =
    appropriatenessTestAttempts?.filter((answers) => answers.status).length ||
    0;
  const appropriatenessFailureCount =
    appropriatenessTestAttempts?.length - appropriatenessPassCount;
  const showManuallyPassButton =
    canEdit &&
    !appropriateness?.isManuallyPassedByAdmin &&
    !appropriateness?.status;
  const showResetAppropriatenessButton =
    canEdit &&
    ((appropriateness && appropriateness.attempt > 0) ||
      appropriateness?.status);

  const appropriatenessStatus =
    getCurrentAppropriatenessStatusLabel(appropriateness);

  return (
    <>
      <AlertDialog
        open={resetAppropriatenessDisclosure.isOpen}
        onClose={resetAppropriatenessDisclosure.onClose}
        title="Warning"
        content="Are you sure you wish to reset this user’s appropriateness test results? This cannot be undone and they will asked to re-complete their appropriateness test when they next make a subscription."
        onConfirm={async () => {
          await resetInvestorAppropriatenessMutation.mutateAsync({
            investorId: id,
          });
          resetAppropriatenessDisclosure.onClose();
        }}
        btnLabels={{ confirm: 'Reset', cancel: 'Go back' }}
      />
      <RecordRow label="Account created by" direction="row">
        {investorDetail.signUpSource &&
          signUpSourceLabel[investorDetail.signUpSource]}
      </RecordRow>
      <RecordRow label="Appropriateness assessment" direction="row">
        {hasAnswerList ? (
          <>
            <span>Passes: {appropriatenessPassCount}</span>
            <span>Failures: {appropriatenessFailureCount}</span>
          </>
        ) : (
          <span>Attempts: {appropriateness?.attempt || 0}</span>
        )}
      </RecordRow>

      <RecordRow label="Appropriateness test results" direction="row">
        {appropriatenessStatus === AppropriatenessStatusLabel.TestReset ? (
          <span>
            {appropriatenessStatus} by {appropriateness?.resetBy} on{' '}
            {appropriateness?.updatedAt.toLabel()}. Re-test available.
          </span>
        ) : (
          <>
            <span>{appropriatenessStatus}</span>
            <span>{appropriateness?.updatedAt.toLabel() ?? 'N/A'}</span>
          </>
        )}
        {canEdit && appropriateness?.attemptBlocked && (
          <Button
            size="extra-small"
            variant="outlined"
            color="primary"
            onClick={() => allowAppropriateness()}
          >
            Allow appropriateness
          </Button>
        )}
        {showResetAppropriatenessButton && (
          <Button
            size="extra-small"
            variant="outlined"
            color="primary"
            onClick={resetAppropriatenessDisclosure.onOpen}
          >
            Reset test
          </Button>
        )}
        {showManuallyPassButton && (
          <Button
            size="extra-small"
            variant="outlined"
            color="primary"
            onClick={() => manuallyPassAppropriateness()}
          >
            Manually pass appropriateness
          </Button>
        )}

        {appropriateness && (
          <Button
            size="extra-small"
            variant="outlined"
            color="primary"
            onClick={downloadQuestion}
          >
            Download appropriateness results
          </Button>
        )}
      </RecordRow>
    </>
  );
};

export default InvestorAppropriateness;
